import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useCallback, useMemo } from 'react';
import { ActorRefFrom } from 'xstate';
import { EmptyState, TableCard } from '~/components';
import { BasicGrid } from '~/components/agGrid/Grid';
import { FacilityFormValues } from '~/features/types';
import { PROVIDER_PAGE_KEYS } from '~/routing/routes';
import { FormDialog } from '../../../components';
import { useEmploymentRef } from '../account/vaultSelectors';
import { CreateEmploymentColumns } from './constants';
import { employmentMachine } from './employment.machine';
import { EmploymentForm } from './EmploymentForm';
import { CreateEmploymentFormValues, EmploymentFormValues } from './types';

const TAGS = CT.employment.table;

const EmploymentNoRowsOverlay = () => (
  <EmptyState
    pageKey={PROVIDER_PAGE_KEYS.employment}
    title="No employment records yet"
    cyTag={TAGS.emptyState}
  />
);

export const EmploymentDetails = () => {
  const { employmentRef } = useEmploymentRef();

  if (!employmentRef) {
    return null;
  }

  return <EmploymentDetailsInner employmentRef={employmentRef} />;
};

const EmploymentDetailsInner = ({
  employmentRef,
}: {
  employmentRef: ActorRefFrom<typeof employmentMachine>;
}) => {
  const employments = useSelector(employmentRef, (state) => state.context.employments);
  const loading = useSelector(employmentRef, (state) => state.context.loading);
  const facilityRef = useSelector(employmentRef, (state) => state.context.facilityRef);
  const form = useSelector(employmentRef, (state) => state.context.employmentForm);
  const { create, edit } = useSelector(employmentRef, (state) => state.context.modal);

  const facilityModal = useSelector(facilityRef, (state) => state?.context.modal);
  const facilityForm = useSelector(facilityRef, (state) => state?.context.form);
  const showDuplicates = useSelector(facilityRef, (state) => state?.context.showDuplicates);
  const duplicates = useSelector(facilityRef, (state) => state?.context.duplicates);
  const pendingFacilityName = useSelector(
    facilityRef,
    (state) => state?.context.pendingValues.label
  );

  const handleEdit = useCallback(
    (id: number) => {
      employmentRef.send({ type: 'ON_EDIT', payload: { id } });
    },
    [employmentRef]
  );

  const handleDelete = useCallback(
    (id: number) => {
      employmentRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } });
    },
    [employmentRef]
  );

  const handleOpen = useCallback(() => {
    employmentRef.send({ type: 'ON_ADD' });
  }, [employmentRef]);

  const handleClose = useCallback(() => {
    employmentRef.send({ type: 'ON_CLOSE_MODAL' });
  }, [employmentRef]);

  const submitEmployment = useCallback(
    (employment: CreateEmploymentFormValues) => {
      employmentRef.send({ type: 'ON_SUBMIT', values: employment });
    },
    [employmentRef]
  );

  const updateEmployment = useCallback(
    (employment: EmploymentFormValues) => {
      employmentRef.send({ type: 'ON_UPDATE', values: employment });
    },
    [employmentRef]
  );

  const handleFacilityOpen = useCallback(
    // [MCV-1638] prefillName is set only by the picker's in-menu `Add "<text>"`
    // row, so the dialog opens with the typed facility name already filled in.
    (values: FacilityFormValues, prefillName?: string) => {
      facilityRef.send({ type: 'ON_OPEN', values: values, prefillName });
    },
    [facilityRef]
  );

  const closeFacilityModal = useCallback(
    () => facilityRef.send({ type: 'ON_CLOSE' }),
    [facilityRef]
  );

  const submitFacilityValues = useCallback(
    (values: FacilityFormValues) => {
      facilityRef.send({ type: 'ON_SUBMIT', values: values });
    },
    [facilityRef]
  );

  const handleSelectExistingFacility = useCallback(
    (facility: FacilityFormValues) => {
      facilityRef.send({ type: 'ON_SELECT_EXISTING', facility });
    },
    [facilityRef]
  );

  const handleConfirmCreateFacility = useCallback(
    () => facilityRef.send({ type: 'ON_CONFIRM_CREATE' }),
    [facilityRef]
  );

  const handleBackToFacilityForm = useCallback(
    () => facilityRef.send({ type: 'ON_BACK_TO_FORM' }),
    [facilityRef]
  );

  const colDefs = useMemo(
    () => CreateEmploymentColumns({ handleDelete, handleEdit }),
    [handleDelete, handleEdit]
  );

  return (
    <TableCard
      mobilePageHeader
      title="Employment History"
      addLabel="Add Employment"
      onAddClick={handleOpen}
      addBtnTag={TAGS.addBtn}
      layoutTag={CT.employment.layout}
    >
      <BasicGrid
        newRecordsTable="employment"
        data={employments}
        loading={loading}
        colDefs={colDefs}
        pagination={true}
        cyTag={TAGS.grid}
        noRowsOverlayComponent={EmploymentNoRowsOverlay}
      />
      <FormDialog
        title={create ? 'Add New Employment' : 'Edit Employment'}
        editing={edit}
        creating={create}
        onClose={handleClose}
      >
        <EmploymentForm
          addEmployment={submitEmployment}
          editEmployment={updateEmployment}
          handleClose={handleClose}
          handleFacilityFormSubmit={submitFacilityValues}
          handleFacilityClose={closeFacilityModal}
          handleFacilityOpen={handleFacilityOpen}
          facilityForm={facilityForm}
          create={create}
          openFacilityModal={facilityModal}
          form={form}
          duplicates={duplicates}
          showDuplicates={showDuplicates}
          pendingFacilityName={pendingFacilityName}
          onSelectExistingFacility={handleSelectExistingFacility}
          onConfirmCreateFacility={handleConfirmCreateFacility}
          onBackToFacilityForm={handleBackToFacilityForm}
        />
      </FormDialog>
    </TableCard>
  );
};
